import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { Bell } from 'lucide-react';
import { API } from '../../constants/api';
import { usePlan } from '../../contexts/PlanContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';

const timeAgo = (dateStr, t) => {
  const then = new Date(dateStr).getTime();
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return t('notificationBell.justNow', 'hace un momento');
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return t('notificationBell.minAgo', `hace ${diffMin} min`).replace('{n}', diffMin);
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return t('notificationBell.hoursAgo', `hace ${diffHr}h`).replace('{n}', diffHr);
  const diffDay = Math.floor(diffHr / 24);
  return t('notificationBell.daysAgo', `hace ${diffDay}d`).replace('{n}', diffDay);
};

const TYPE_DOT_COLOR = { success: '#16A34A', error: '#DC2626', undo: '#6B7280' };

const NotificationBell = ({ setActiveTab }) => {
  const { hasFeature } = usePlan();
  const { t } = useLanguage();
  const { user } = useAuth();
  const { history } = useNotification();
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);

  // "Unread" for history entries (leads already have their own `estado`
  // field for that) is tracked as "posted after the last time the bell was
  // opened" — a simple, per-user timestamp instead of per-item read flags.
  const lastSeenKey = user?.id ? `notification_last_seen_${user.id}` : null;
  const [lastSeen, setLastSeen] = useState(() => {
    if (!lastSeenKey) return 0;
    try { return Number(localStorage.getItem(lastSeenKey)) || 0; } catch { return 0; }
  });
  const [coords, setCoords] = useState({ top: 0, right: 0 });
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !hasFeature('leads')) return undefined;

    const fetchRecent = () => {
      axios.get(`${API}/leads/me/recent`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => setLeads(res.data || []))
        .catch(() => {});
    };

    fetchRecent();
    const id = setInterval(fetchRecent, 60000);
    return () => clearInterval(id);
  }, [hasFeature]);

  useEffect(() => {
    const onClickOutside = (e) => {
      const insideButton = wrapRef.current && wrapRef.current.contains(e.target);
      const insideDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!insideButton && !insideDropdown) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // The dropdown is portaled to <body> and positioned with `fixed` viewport
  // coordinates (computed from the bell button's own rect) instead of
  // `absolute` inside the normal DOM flow. Dashboard pages force
  // `overflow-x:hidden` on their outer container on mobile (App.css) to
  // stop unrelated horizontal-scroll bugs — an `absolute` dropdown nested
  // inside that container gets clipped by it. Rendering outside that
  // subtree sidesteps the clipping entirely, on every page, without
  // touching that shared mobile CSS rule.
  const reposition = () => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setCoords({ top: rect.bottom + 8, right: Math.max(8, window.innerWidth - rect.right) });
  };

  const toggleOpen = () => {
    if (!open) {
      reposition();
      const now = Date.now();
      setLastSeen(now);
      if (lastSeenKey) {
        try { localStorage.setItem(lastSeenKey, String(now)); } catch {}
      }
    }
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  // Merge live leads (real backend data, gated by the `leads` feature) with
  // the toast history from NotificationContext (available to every plan) —
  // one chronological list instead of two separate notification surfaces.
  const combined = [
    ...leads.map((lead) => ({ kind: 'lead', key: `lead-${lead.id}`, timestamp: new Date(lead.fecha_identificado).getTime() || 0, lead })),
    ...history.map((h) => ({ kind: 'notification', key: `note-${h.id}`, timestamp: h.timestamp, entry: h })),
  ].sort((a, b) => b.timestamp - a.timestamp).slice(0, 20);

  const unreadCount = combined.filter((item) => (
    item.kind === 'lead' ? item.lead.estado === 'Nuevo' : item.timestamp > lastSeen
  )).length;

  const listContent = (
    <div style={{ maxHeight: isMobile ? '100%' : 320, overflowY: 'auto' }}>
      {combined.length === 0 && (
        <div style={{ padding: '24px 14px', textAlign: 'center', fontSize: 12.5, color: '#A3A3A3' }}>
          {t('notificationBell.noRecentActivity', 'Sin actividad reciente')}
        </div>
      )}
      {combined.map((item) => (
        item.kind === 'lead' ? (
          <button
            key={item.key}
            onClick={() => { setActiveTab('leads'); setOpen(false); }}
            style={{
              width: '100%', display: 'flex', flexDirection: 'column', gap: 2, padding: '10px 14px',
              border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer',
              borderBottom: '1px solid rgba(0,0,0,0.04)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#FAFAFA'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <span style={{ fontSize: 12.5, color: '#1d1d1f' }}>
              {t('notificationBell.newLead', 'Nuevo lead:')} <strong>{item.lead.nombre || t('notificationBell.noName', 'Sin nombre')}</strong>
            </span>
            <span style={{ fontSize: 11, color: '#A3A3A3' }}>
              {item.lead.profile_name} · {timeAgo(item.lead.fecha_identificado, t)}
            </span>
          </button>
        ) : (
          <div
            key={item.key}
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 14px',
              borderBottom: '1px solid rgba(0,0,0,0.04)',
            }}
          >
            <span style={{
              width: 6, height: 6, borderRadius: '50%', marginTop: 5, flexShrink: 0,
              background: TYPE_DOT_COLOR[item.entry.type] || TYPE_DOT_COLOR.undo,
            }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <span style={{ fontSize: 12.5, color: '#1d1d1f' }}>{item.entry.message}</span>
              <span style={{ fontSize: 11, color: '#A3A3A3' }}>{timeAgo(item.entry.timestamp, t)}</span>
            </div>
          </div>
        )
      ))}
    </div>
  );

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        onClick={toggleOpen}
        className="flex items-center justify-center"
        style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid #E5E5E5', background: '#fff', color: '#525252', flexShrink: 0, transition: 'background 0.15s, border-color 0.15s, color 0.15s', position: 'relative', cursor: 'pointer' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#FAFAFA'; e.currentTarget.style.borderColor = '#D4D4D4'; e.currentTarget.style.color = '#0A0A0A'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#E5E5E5'; e.currentTarget.style.color = '#525252'; }}
        title={t('notificationBell.title', 'Notificaciones')}
      >
        <Bell className="w-4 h-4" strokeWidth={1.75} />
        {unreadCount > 0 && (
          <span style={{ position: 'absolute', top: 4, right: 4, width: 6, height: 6, borderRadius: '50%', background: '#DC2626' }} />
        )}
      </button>

      {open && ReactDOM.createPortal(
        isMobile ? (
          <div
            style={{
              position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
            }}
            onClick={() => setOpen(false)}
          >
            <div
              ref={dropdownRef}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%', maxWidth: 340, maxHeight: '70vh', display: 'flex', flexDirection: 'column',
                background: '#fff', borderRadius: 16, boxShadow: '0 24px 60px rgba(0,0,0,0.25)', overflow: 'hidden',
              }}
            >
              <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#1d1d1f' }}>{t('notificationBell.recentActivity', 'Actividad reciente')}</span>
                <button
                  onClick={() => setOpen(false)}
                  aria-label={t('notificationBell.close', 'Cerrar')}
                  style={{ width: 26, height: 26, borderRadius: '50%', background: 'rgba(0,0,0,0.06)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6e6e73', cursor: 'pointer', flexShrink: 0 }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {listContent}
            </div>
          </div>
        ) : (
          <div
            ref={dropdownRef}
            style={{
              position: 'fixed', top: coords.top, right: coords.right, width: 280, maxWidth: 'calc(100vw - 16px)',
              background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12,
              boxShadow: '0 8px 30px rgba(0,0,0,0.15)', zIndex: 500, overflow: 'hidden',
            }}
          >
            <div style={{ padding: '12px 14px', borderBottom: '1px solid rgba(0,0,0,0.06)', fontSize: 13, fontWeight: 600, color: '#1d1d1f' }}>
              {t('notificationBell.recentActivity', 'Actividad reciente')}
            </div>
            {listContent}
          </div>
        ),
        document.body
      )}
    </div>
  );
};

export default NotificationBell;
